import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Heart, Check, Loader2, Image, Video, Mic, Square, Circle } from "lucide-react";
import api from "@/lib/api";
import { chunkedUpload } from "@/lib/upload";
import { Logo } from "@/components/Common";

function kindFor(file) {
  const t = file.type || "";
  if (t.startsWith("image/")) return "photo";
  if (t.startsWith("video/")) return "video";
  if (t.startsWith("audio/")) return "voice";
  return "document";
}

export default function StoryCollect() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ contributor_name: "", who_what: "", when_text: "", memory_text: "" });
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null); const chunksRef = useRef([]); const streamRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/stories/collect/${slug}`).then((r) => setInfo(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const recordVoice = async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream; chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => { setVoiceBlob(new Blob(chunksRef.current, { type: "audio/webm" })); stream.getTracks().forEach((t) => t.stop()); setRecording(false); };
      recRef.current = rec; rec.start(); setRecording(true);
    } catch { toast.error("We couldn't reach your microphone."); }
  };

  const submit = async () => {
    if (!form.contributor_name.trim()) return toast.error("Please tell us your name");
    if (!file && !form.memory_text.trim() && !voiceBlob) return toast.error("Add a photo/video or share a memory");
    setSubmitting(true);
    try {
      let file_id = null, kind = "document", voice_id = null;
      if (file) { const up = await chunkedUpload(file, file.name, file.type); file_id = up.file_id; kind = kindFor(file); }
      if (voiceBlob) { const up = await chunkedUpload(voiceBlob, "memory.webm", "audio/webm"); voice_id = up.file_id; if (!file) { kind = "voice"; file_id = up.file_id; } }
      await api.post(`/stories/collect/${slug}/contribute`, {
        kind, file_id: file_id || voice_id || "", contributor_name: form.contributor_name,
        who_what: form.who_what, when_text: form.when_text, memory_text: form.memory_text,
        memory_voice_file_id: voice_id,
      });
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
    finally { setSubmitting(false); }
  };

  if (notFound) return <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))]"><p className="text-[hsl(var(--muted-foreground))]">This collection link is no longer available.</p></div>;
  if (!info) return <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))]"><Loader2 className="animate-spin text-[hsl(var(--accent))]" /></div>;

  if (done) return (
    <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))] px-4">
      <div className="max-w-md text-center" data-testid="collect-thankyou">
        <div className="h-14 w-14 mx-auto grid place-items-center rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"><Check size={26} /></div>
        <h1 className="mt-4 font-display text-2xl font-semibold">Thank you</h1>
        <p className="mt-2 text-[hsl(var(--muted-foreground))]">Your memory has been shared with {info.honoree_name}'s family. They'll review and add it to the story.</p>
        <button data-testid="collect-add-another" onClick={() => { setDone(false); setFile(null); setVoiceBlob(null); setForm({ contributor_name: form.contributor_name, who_what: "", when_text: "", memory_text: "" }); }}
          className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))]">Share another memory</button>
      </div>
    </div>
  );

  const inputCls = "w-full h-12 rounded-xl px-4 bg-[hsl(var(--card))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]";

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="story-collect">
      <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]"><div className="max-w-lg mx-auto px-4 h-16 flex items-center"><Logo /></div></div>
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-8">
        <div className="text-center">
          <p className="font-mono text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Family Memory Collection</p>
          <h1 className="mt-1 font-display text-3xl font-semibold">{info.title}</h1>
          <p className="mt-2 text-[hsl(var(--muted-foreground))]">Help tell {info.honoree_name}'s story. Add a photo or video and share the memory behind it.</p>
          <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-[hsl(var(--accent))] font-semibold"><Heart size={14} /> {info.progress?.items || 0} items · {info.progress?.memories || 0} memories gathered</p>
        </div>

        <div className="mt-6 space-y-4">
          <input data-testid="collect-name-input" value={form.contributor_name} onChange={(e) => set("contributor_name", e.target.value)} placeholder="Your name *" className={inputCls} />

          <input ref={fileRef} type="file" accept="image/*,video/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="collect-file-input" />
          <button data-testid="collect-choose-file" onClick={() => fileRef.current?.click()} className="w-full rounded-2xl border-2 border-dashed border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 flex flex-col items-center gap-2 hover:border-[hsl(var(--accent))] transition-colors">
            {file ? (file.type.startsWith("video/") ? <Video className="text-[hsl(var(--accent))]" /> : <Image className="text-[hsl(var(--accent))]" />) : <Upload className="text-[hsl(var(--muted-foreground))]" />}
            <span className="text-sm font-medium">{file ? file.name : "Tap to add a photo or video from your phone"}</span>
          </button>

          <input data-testid="collect-whowhat-input" value={form.who_what} onChange={(e) => set("who_what", e.target.value)} placeholder="Who or what is in this?" className={inputCls} />
          <input data-testid="collect-when-input" value={form.when_text} onChange={(e) => set("when_text", e.target.value)} placeholder="Roughly when was this? (e.g. summer 1974)" className={inputCls} />
          <textarea data-testid="collect-memory-input" value={form.memory_text} onChange={(e) => set("memory_text", e.target.value)} placeholder="Share the memory behind it…" rows={4} className="w-full rounded-xl px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />

          <button data-testid="collect-record-voice" onClick={recordVoice} className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl border font-semibold ${recording ? "bg-[hsl(var(--destructive))] text-white border-transparent" : voiceBlob ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"}`}>
            {recording ? <><Square size={16} fill="currentColor" /> Stop recording</> : voiceBlob ? <><Check size={16} /> Voice memory saved — re-record</> : <><Mic size={16} /> Or record the memory in your voice</>}
          </button>

          <button data-testid="collect-submit" onClick={submit} disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-base hover:opacity-90 disabled:opacity-60">
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <Heart size={18} />} Share this memory
          </button>
        </div>
      </div>
    </div>
  );
}
